import {
  Checkbox,
  FormControlLabel,
  InputLabel,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { ChangeEvent, CSSProperties, useState } from 'react'

import ModalActionContainer from 'components/containers/modalActionContainer'
import { helper } from 'helper'
import { WeekKey } from 'helper/week'
import { WorkPatternContent } from 'types/workPattern'
import { colors } from 'variables'

interface Props {
  onCancel: () => void
  onAdd: (data: WorkPatternContent) => void
  onUpdate: (id: string, data: WorkPatternContent) => void
  defaultValue?: WorkPatternContent & { id: string }
}

const DEFAULT_TIME = {
  startTime: '08:00',
  endTime: '17:00',
}

const timeValidation = (time: string | null) => {
  if (time === null) {
    return ''
  }

  const formatted = helper.formatter.durationParse(time)
  if (formatted === null) {
    return '形式が正しくありません(HH:mm)'
  }

  return ''
}

const minValueValidation = (num: string, minValue: number) => {
  const parsedNum = parseInt(num, 10)
  if (isNaN(parsedNum)) {
    return '数値を入力してください'
  }

  return parsedNum < minValue ? `人数は${minValue}以上で入力してください` : ''
}

export default function WorkPatternSettingModal(props: Props) {
  const [workPatternName, setWorkPatternName] = useState(
    props.defaultValue?.name ?? '',
  )

  const timeToStr = (time: number | undefined) => {
    if (time == null) return null
    const duration = helper.time.fromMinutes(time)
    return helper.formatter.durationFormat(duration)
  }

  const [startTime, setStartTime] = useState<string | null>(
    timeToStr(props.defaultValue?.startTime) ?? DEFAULT_TIME.startTime,
  )
  const [startTimeHelperText, setStartTimeHelperText] = useState('')

  const [endTime, setEndTime] = useState<string | null>(
    timeToStr(props.defaultValue?.endTime) ?? DEFAULT_TIME.endTime,
  )
  const [endTimeHelperText, setEndTimeHelperText] = useState('')

  const [assignNum, setAssignNum] = useState<string | null>(
    props.defaultValue?.assignNum?.toString() ?? null,
  )
  const [assignNumHelperText, setAssignNumHelperText] = useState('')

  const emptyDayOfWeeks = Array.from({ length: 8 }, () => false)
  const [dayOfWeeks, setDayOfWeeks] = useState(
    props.defaultValue?.daysOfWeeks ?? emptyDayOfWeeks,
  )

  const handleChangeWorkPatternName = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    setWorkPatternName(name)
  }

  const handleChangeStartTime = (e: ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value
    setStartTimeHelperText(timeValidation(time))
    setStartTime(time)
  }

  const handleChangeEndTime = (e: ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value
    setEndTimeHelperText(timeValidation(time))
    setEndTime(time)
  }

  const handleChangeAssignNum = (e: ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value
    setAssignNum(num)
    setAssignNumHelperText(minValueValidation(num, 0))
  }

  const handleChangeWeek =
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const newDayOfWeeks = [...dayOfWeeks]
      newDayOfWeeks[index] = e.target.checked
      setDayOfWeeks(newDayOfWeeks)
    }

  const handleSave = () => {
    const helperTexts = [
      startTimeHelperText,
      endTimeHelperText,
      assignNumHelperText,
    ]

    if (helperTexts.some((helperText) => helperText !== '')) {
      // バリデーション違反
      return
    }

    // === 強制型変換 ===
    if (workPatternName === '') return

    if (startTime === null) return
    const validatedStartTime = helper.formatter.durationParse(startTime)
    if (validatedStartTime === null) return

    if (endTime === null) return
    const validatedEndTime = helper.formatter.durationParse(endTime)
    if (validatedEndTime === null) return

    if (assignNum === null) return
    const validatedAssignNum = parseInt(assignNum ?? '', 10)
    if (isNaN(validatedAssignNum)) return

    if (props.defaultValue == null) {
      props.onAdd({
        name: workPatternName,
        assignNum: validatedAssignNum,
        startTime: helper.time.toMinutes(validatedStartTime),
        endTime: helper.time.toMinutes(validatedEndTime),
        daysOfWeeks: dayOfWeeks,
      })
    } else {
      props.onUpdate(props.defaultValue.id, {
        name: workPatternName,
        assignNum: validatedAssignNum,
        startTime: helper.time.toMinutes(validatedStartTime),
        endTime: helper.time.toMinutes(validatedEndTime),
        daysOfWeeks: dayOfWeeks,
      })
    }
  }

  return (
    <Box>
      <Box style={formContainerStyle}>
        <TextField
          variant="standard"
          id="workPatternName"
          label="勤務パターン名"
          value={workPatternName}
          onChange={handleChangeWorkPatternName}
        />

        <Box style={timeContainerStyle}>
          <TextField
            variant="standard"
            id="startTime"
            label="開始時間"
            value={startTime ?? ''}
            onChange={handleChangeStartTime}
            helperText={startTimeHelperText}
          />
          <Typography
            style={{
              alignSelf: 'flex-end',
            }}
          >
            〜
          </Typography>
          <TextField
            variant="standard"
            id="endTime"
            label="終了時間"
            value={endTime ?? ''}
            onChange={handleChangeEndTime}
            helperText={endTimeHelperText}
          />
        </Box>

        <TextField
          variant="standard"
          id="assignNum"
          label="人数"
          value={assignNum ?? ''}
          onChange={handleChangeAssignNum}
          helperText={assignNumHelperText}
        />

        <Box>
          <InputLabel>曜日</InputLabel>
          <Box style={weekContainerStyle}>
            {helper.week.dayOfWeeks().map((dayOfWeek, i) => (
              <FormControlLabel
                style={weekCheckboxContainerStyle}
                componentsProps={{
                  typography: weekColorStyle(dayOfWeek),
                }}
                key={`week-${i}`}
                control={
                  <Checkbox
                    style={weekCheckboxStyle}
                    checked={dayOfWeeks[i]}
                    onChange={handleChangeWeek(i)}
                  />
                }
                label={dayOfWeek}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <ModalActionContainer>
        <Button onClick={props.onCancel} variant="outlined">
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained">
          保存
        </Button>
      </ModalActionContainer>
    </Box>
  )
}

const formContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const timeContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-start',
}

const weekContainerStyle: CSSProperties = {
  display: 'flex',
}

const weekCheckboxContainerStyle: CSSProperties = {
  marginRight: '16px',
}

const weekCheckboxStyle: CSSProperties = {
  padding: 0,
}

const weekColorStyle = (week: WeekKey): TypographyProps => {
  if (week === '日' || week === '祝') {
    return {
      // 日曜日
      color: colors.WEEK.SUN_COLOR,
    }
  }

  if (week === '土') {
    return {
      // 土曜日
      color: colors.WEEK.SAT_COLOR,
    }
  }

  return {
    // 平日 (色の上書き無し)
  }
}
